import type { IModuleState, ISessionState, ViewStateMap } from '@platform/core/types';

const SESSION_KEY = 'po.session.v1';
const TAB_WORKSPACE_KEY = 'po.workspace.v1';
const VIEW_STATE_KEY = 'po.viewState.v1';
const moduleKeyOf = (moduleKey: string): string => `po.module.${moduleKey}.v1`;

const isModuleState = (value: unknown): value is IModuleState =>
  typeof value === 'object' && value !== null &&
  typeof (value as IModuleState).lists === 'object' && (value as IModuleState).lists !== null &&
  typeof (value as IModuleState).selected === 'object' && (value as IModuleState).selected !== null;

/**
 * Персистентність лише клієнтського стану: сесія, viewState і стан кожного пакета модуля окремим ключем (localStorage). Поточний робочий
 * простір (ADR-028: його немає в адресі) — свій у кожній вкладці (sessionStorage `po.workspace.v1`, переживає перезавантаження); нова
 * вкладка бере останній використаний (currentWorkspaceId сесії в localStorage). Серверні дані ніколи не зберігаються локально.
 */
export const persistence = {
  loadSession(): Partial<ISessionState> | null {
    const session = read<Partial<ISessionState>>(SESSION_KEY);
    const tab = read<{ id: string | null }>(TAB_WORKSPACE_KEY, true);
    return tab ? { ...session, currentWorkspaceId: tab.id } : session;
  },
  saveSession(state: ISessionState): void {
    write(SESSION_KEY, state);
    write(TAB_WORKSPACE_KEY, { id: state.currentWorkspaceId }, true);
  },
  /** Останній використаний простір для нових вкладок (посилання в інший простір, відкрите в новій вкладці), поточна вкладка — без змін. */
  rememberWorkspace(workspaceId: string): void { write(SESSION_KEY, { ...read<Partial<ISessionState>>(SESSION_KEY), currentWorkspaceId: workspaceId }); },
  loadViewState(): ViewStateMap | null { return read<ViewStateMap>(VIEW_STATE_KEY); },
  saveViewState(state: ViewStateMap): void { write(VIEW_STATE_KEY, state); },
  /** Збережений стан зрізу пакета модуля (`po.module.<ключ>.v1`) або null, якщо його нема чи форма не схожа на IModuleState. */
  loadModuleState<S extends IModuleState>(moduleKey: string): S | null {
    const value = read<S>(moduleKeyOf(moduleKey));
    return isModuleState(value) ? value : null;
  },
  saveModuleState(moduleKey: string, state: IModuleState): void { write(moduleKeyOf(moduleKey), state); },
};

function read<T>(key: string, tab = false): T | null {
  try {
    const raw = (tab ? sessionStorage : localStorage).getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown, tab = false): void {
  try { (tab ? sessionStorage : localStorage).setItem(key, JSON.stringify(value)); } catch { /* приватний режим — ігноруємо */ }
}
